import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { api, breakGlassOffered, clinicHeader, readError, type ApiError } from "../api/client";
import { AllergyBanner } from "../components/AllergyBanner";
import { BreakGlass } from "../components/BreakGlass";
import { CheckInButton } from "../components/CheckInButton";
import { ErrorNote } from "../components/ErrorNote";
import { IdentifierField } from "../components/IdentifierField";
import { ImmunisationPanel } from "../components/ImmunisationPanel";
import { IssuedPanel } from "../components/IssuedPanel";
import { ProblemList } from "../components/ProblemList";
import { RecallPrompt } from "../components/RecallPrompt";
import { RecordForm } from "../components/RecordForm";
import { RecordTable } from "../components/RecordTable";
import { UploadPanel } from "../components/UploadPanel";
import { VisitPanel } from "../components/VisitPanel";
import { shapesFor } from "../recordFields";
import { TabBar, TabPanel, type Tab } from "../components/TabBar";
import { useSession } from "../useSession";
import { ACCESS_ROUTE_LABELS, RECORD_TYPE_LABELS, type Chart as ChartData } from "../types";

const GROUPS = ["observation", "condition", "medication", "document"] as const;

/**
 * One patient's chart.
 *
 * A refusal is a state of this screen, not an error page. The server answers a
 * 403 with the ways forward (CLN-F-040), so a doctor with no relationship sees
 * the break-glass panel here rather than a dead end.
 *
 * Nothing on this screen filters records. What arrives is what this caller may
 * see: a restricted record is absent from the response, never hidden by CSS.
 */
export function Chart() {
  const { id } = useParams<{ id: string }>();
  const { clinic } = useSession();
  const [chart, setChart] = useState<ChartData | null>(null);
  const [refusal, setRefusal] = useState<ApiError | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  /* Which section is open. Overview, until somebody chooses otherwise. */
  const [tab, setTab] = useState("overview");

  /**
   * Fetch the chart. `showProgress` only on the first load of a patient.
   *
   * A refresh used to swap the whole screen for "Opening the chart...", which
   * unmounted everything open on it. A doctor who accepted one reading out of
   * a report had the review close under them and had to find their place
   * again; the same happened on every correction and every release. A chart
   * already on the screen is refreshed in place.
   */
  const load = useCallback(async (showProgress = false) => {
    if (!id) return;
    if (showProgress) setLoading(true);

    const { data, error: failed } = await api.GET("/portal/patients/{id}/chart", {
      params: { path: { id }, header: clinicHeader() },
    });
    setLoading(false);

    if (failed) {
      const problem = readError(failed);
      setChart(null);
      if (breakGlassOffered(problem)) {
        setRefusal(problem);
        setError(null);
      } else {
        setRefusal(null);
        setError(problem.message);
      }
      return;
    }

    setRefusal(null);
    setError(null);
    setChart(data as ChartData);
  }, [id]);

  useEffect(() => {
    void load(true);
  }, [load]);

  if (loading) return <p className="empty">Opening the chart…</p>;

  if (refusal && id) {
    return <BreakGlass patientId={id} refusal={refusal} onGranted={load} />;
  }

  if (error) {
    return (
      <section>
        <ErrorNote message={error} />
        <Link to="/patients">Back to patients</Link>
      </section>
    );
  }

  if (!chart || !id) return null;

  const isDoctor = clinic?.role === "doctor";
  // What this role may write. A nurse records measurements and nothing else
  // (CLN-F-013); the server checks the same rule.
  const writes = shapesFor(clinic?.role);
  const records = chart.records ?? [];

  /* The strip is built from what this chart holds. A tab that opens on
     "nothing here yet" is a tab that wasted a click, and the index this
     replaced already worked that way. Visits, vaccinations and issued
     documents are always offered, because each owns its own empty state and
     is also where a clinician goes to add the first one. */
  const tabs: Tab[] = [
    { id: "overview", label: "Overview" },
    { id: "visits", label: "Visits" },
    ...GROUPS.filter((group) => records.some((record) => record.type === group)).map(
      (group) => ({
        id: group,
        label: RECORD_TYPE_LABELS[group] ?? group,
        count: records.filter((record) => record.type === group).length,
      }),
    ),
    { id: "vaccinations", label: "Vaccinations" },
    { id: "issued", label: "Issued" },
  ];

  return (
    <section>
      <Link to="/patients" className="back">
        Patients
      </Link>

      {/* The band brief 03's board opens with: the patient at editorial
          scale on the clinic's own photograph, and the four things that
          identify them laid out in labelled pairs underneath rather than
          run together in one grey sentence.

          "No MRN" is written out, as section 5 asks. A missing MRN is a fact
          about the record; a blank is a question about the page. */}
      <header className="screen-band">
        <div className="screen-band-photo" aria-hidden="true" />
        <div className="screen-band-words">
          <h1>{chart.patient?.full_name}</h1>
          <dl className="chart-identity">
            <div>
              <dt>MRN</dt>
              <dd>{chart.patient?.mrn || "No MRN"}</dd>
            </div>
            <div>
              <dt>Born</dt>
              <dd>{chart.patient?.birth_date || "Not recorded"}</dd>
            </div>
          </dl>
        </div>
      </header>

      {/* How a Malaysian clinic finds this person. Whole, not masked: the
          clinician with the chart open is holding the card (ADR-019 point 6).
          Only the front desk may change it (ADR-008 matrix). */}
      <IdentifierField
        patientId={id}
        type={chart.patient?.identifier_type ?? ""}
        value={chart.patient?.identifier_value ?? ""}
        canEdit={clinic?.role === "front_desk" || clinic?.role === "clinic_admin"}
        onChanged={load}
      />

      <p className="route">
        {ACCESS_ROUTE_LABELS[chart.access_via ?? ""] ?? ""}{" "}
        {/* Never a gate. A doctor opens a visit whether this was pressed
            or not (SCH-F-041). */}
        <CheckInButton patientId={id} onChanged={load} />
      </p>

      {/* Two columns on a wide screen, one below it.

          Nine panels stacked in a single column meant scrolling past the
          allergies to reach the visit history, and CLN-F-071 asks for them
          above every control that could prescribe and never collapsed.
          Sticking them to the left satisfies that better than ordering did:
          they stay on screen while the history scrolls. */}
      <div className="chart-layout">
        <aside className="chart-rail">
          {/* Above every control that could prescribe, and never collapsed
              (CLN-F-071). A nurse may write to it as well as a doctor. */}
          <AllergyBanner patientId={id} canRecord={isDoctor || clinic?.role === "nurse"} />

          {/* CLN-F-117: a recall the doctor never sees is a recall that never closes. */}
          <RecallPrompt patientId={id} isDoctor={isDoctor} onChanged={load} />

          {/* CLN-F-123: what the patient lives with, before the visit history. */}
          <ProblemList patientId={id} isDoctor={isDoctor} />
        </aside>

        <div className="chart-main">
          {/* The chart's sections, as brief 03's board organises them. It was
              one column run end to end with an index at the top — which is
              the arrangement the index existed to apologise for: finding the
              last blood pressure meant scrolling past the certificates.

              Built from what this chart actually holds, so it never offers an
              empty section; the index followed the same rule. Overview is
              always first, so opening a chart shows the recent of everything
              before anybody clicks. */}
          <TabBar
            tabs={tabs}
            active={tab}
            onChange={setTab}
            label="Sections of this chart"
          />

          <TabPanel id="overview" active={tab}>
            {/* What a clinician wants in the first five seconds, and the two
                ways to add to the chart. Everything here is the real record
                cut short rather than a summary written separately from it: a
                summary that can disagree with the thing it summarises is
                worse than no summary at all. */}
            <div className="panels">
              {writes.length > 0 &&
                (adding ? (
                  <RecordForm
                    mode={{ kind: "create", patientId: id }}
                    role={clinic?.role}
                    onDone={() => {
                      setAdding(false);
                      void load();
                    }}
                    onCancel={() => setAdding(false)}
                  />
                ) : (
                  <button type="button" onClick={() => setAdding(true)}>
                    Add to the chart
                  </button>
                ))}

              <UploadPanel patientId={id} onUploaded={load} canUpload={isDoctor} />
            </div>

            {records.length === 0 && (
              <p className="empty">This chart has no records yet.</p>
            )}

            {GROUPS.map((group) => {
              const rows = records.filter((record) => record.type === group);
              if (rows.length === 0) return null;
              const shown = rows.slice(0, 3);
              return (
                <div key={group}>
                  <RecordTable
                    title={RECORD_TYPE_LABELS[group] ?? group}
                    records={shown}
                    canRelease={isDoctor}
                    role={clinic?.role}
                    onChanged={load}
                  />
                  {rows.length > shown.length && (
                    <p className="chart-more">
                      <button type="button" className="link" onClick={() => setTab(group)}>
                        All {rows.length}{" "}
                        {(RECORD_TYPE_LABELS[group] ?? group).toLowerCase()}
                      </button>
                    </p>
                  )}
                </div>
              );
            })}
          </TabPanel>

          <TabPanel id="visits" active={tab}>
            <VisitPanel patientId={id} isDoctor={isDoctor} onChanged={load} />
          </TabPanel>

          {/* A nurse records one, because a nurse gives one (CLN-F-130). */}
          <TabPanel id="vaccinations" active={tab}>
            <ImmunisationPanel
              patientId={id}
              canRecord={isDoctor || clinic?.role === "nurse"}
            />
          </TabPanel>

          <TabPanel id="issued" active={tab}>
            <IssuedPanel patientId={id} isDoctor={isDoctor} />
          </TabPanel>

          {GROUPS.map((group) => {
            const rows = records.filter((record) => record.type === group);
            if (rows.length === 0) return null;
            return (
              <TabPanel id={group} active={tab} key={group}>
                <RecordTable
                  title={RECORD_TYPE_LABELS[group] ?? group}
                  records={rows}
                  canRelease={isDoctor}
                  role={clinic?.role}
                  onChanged={load}
                />
              </TabPanel>
            );
          })}
        </div>
      </div>
    </section>
  );
}
